import type { Transition } from "motion/react"

/**
 * The jacket feed's two movements — defined once, imported everywhere
 * (BRIEF v2 §3.4). Nothing else exists: any other spring or bezier inside
 * the shells is a bug the audit greps for.
 *
 * One sanctioned exemption: the Up Next rule (up-next.tsx) draws with
 * `ease: "linear"`. That is a timer readout, not a movement — paper easing
 * would lie about the remaining time. Paper stays on the panel's enter/exit.
 */

/**
 * Paper — anything that moves like paper (a leaf, a sleeve, a page).
 * Duration-based, zero overshoot. JS mirror of the CSS token `--ease-paper`
 * in styles/theme.css — keep the two in sync by hand.
 */
export const EASE_PAPER: [number, number, number, number] = [0.22, 1, 0.36, 1]

/** Paper's house duration — the center of the 320–420ms band. */
export const PAPER: Transition = { duration: 0.38, ease: EASE_PAPER }

/**
 * Media Z — the one house spring, for media travelling between ambient and
 * active. Perceived overshoot ≤ 2%; never use motion's default spring.
 */
export const springMediaZ = {
  type: "spring",
  stiffness: 420,
  damping: 38,
  mass: 1,
} as const

/**
 * prefers-reduced-motion fallback: the 200ms crossfade. (The other named
 * fallback is an instant cut — `{ duration: 0 }` at the call site.)
 */
export const REDUCED_FADE: Transition = { duration: 0.2 }

/**
 * The record's spin (open audio stage). 33⅓ rpm = one revolution / 1.8s — a
 * turntable readout of playback, not paper movement, so it eases `linear` and
 * repeats forever, the same sanctioned exemption as the Up Next rule. Gated to
 * actual play state and dropped entirely under reduced motion at the call site.
 */
export const VINYL_RPM_SEC = 1.8

/**
 * Type entrance (§3.4): 8px rise + opacity in, 40ms stagger per block
 * (title, byline, each notes paragraph — per-wrapped-line is unknowable
 * before layout, so per-block is the documented interpretation). Never
 * scale, never blur.
 * Reduced motion: a single 200ms fade — no rise, no stagger.
 */
export const lineVariants = (reduce: boolean) => ({
  hidden: reduce ? { opacity: 0 } : { opacity: 0, y: 8 },
  shown: (i: number) =>
    reduce
      ? { opacity: 1, transition: REDUCED_FADE }
      : {
          opacity: 1,
          y: 0,
          transition: { delay: i * 0.04, duration: 0.32, ease: EASE_PAPER },
        },
})

/**
 * The strip of each neighbour plate that bleeds in at the viewport's top and
 * bottom (§3.6) — the vertical carousel's read. Slides are sized with the
 * literal class `basis-[calc(100%-80px)]` (Tailwind can't extract an
 * interpolated value) — 40px each end with the carousel centred; keep the
 * two numbers in sync (80 = 2·40).
 */
export const PEEK_PX = 40
